// Canonical intake_path labels. Mirrored across Deliveries / DeliveryDetail
// / GrowerDetail / Yard / LoadingBay so renaming the labels happens in one
// place. Per operator request: NOT_DRENCHED reads as "OG (Orchard Grade)",
// REBINNED keeps its name, etc.
//
// The legacy values (DIRECT_TO_PRODUCTION / DRENCHING / EXTERNAL_COLOUR_UP)
// pre-date the migration-119 simplification but still appear on historical
// rows, so we keep them in the map.

import type { IntakePath } from '@/types/database'

export const INTAKE_PATH_LABEL: Record<IntakePath, string> = {
  NOT_DRENCHED:         'OG (Orchard Grade)',
  DRENCHED:             'Already Drenched',
  REBINNED:             'Rebinned',
  DIRECT_TO_COLDROOM:   'Straight to Cold Room',
  // Legacy
  DIRECT_TO_PRODUCTION: 'Direct to Production',
  DRENCHING:            'Drenching Required',
  EXTERNAL_COLOUR_UP:   'External Colour-Up',
}

// Returns a human label for any string value (defends against unknown
// legacy values by falling back to a tidied version of the raw string).
export function intakePathLabel(value: string | null | undefined): string {
  if (!value) return ''
  if (value in INTAKE_PATH_LABEL) return INTAKE_PATH_LABEL[value as IntakePath]
  return value.replace(/_/g, ' ').toLowerCase()
}

// ---------------------------------------------------------------------------
// Fruit that is never drenched (mig 20261025191001)
//
// commodities.uses_drench = false (stone fruit) means the "Fruit on arrival"
// question is not asked. Every line of that fruit saves as DIRECT_TO_COLDROOM,
// whatever the picker holds, and lands IN_STOCK. Some fruit we can't name yet:
// no variety picked, a typo, or the list still loading or offline. That fruit
// keeps the question, which is what every delivery got before, so only an
// explicit false takes it away.
// ---------------------------------------------------------------------------

export function commodityUsesDrench(
  commodity: { uses_drench?: boolean | null } | null | undefined,
): boolean {
  return commodity?.uses_drench !== false
}

/** The intake_path one fruit line saves with. */
export function intakePathForLine(picked: IntakePath, usesDrench: boolean): IntakePath {
  return usesDrench ? picked : 'DIRECT_TO_COLDROOM'
}

/**
 * What a delivery's edit screen offers for its intake path. Drenched fruit
 * gets the three drench answers; never-drenched fruit gets Straight to Cold
 * Room. The row's own value always stays in the list, first when it is none
 * of those, so the select shows what the row holds rather than silently
 * showing the first option (an older peach load on OG, a legacy path).
 */
export function intakePathOptions(usesDrench: boolean, current: IntakePath): IntakePath[] {
  const offered: IntakePath[] = usesDrench
    ? ['NOT_DRENCHED', 'DRENCHED', 'REBINNED']
    : ['DIRECT_TO_COLDROOM']
  return offered.includes(current) ? offered : [current, ...offered]
}

/**
 * Which fruit a delivery line is. The variety goes through the alias map first:
 * it is what the truck carries, and what sanitise_delivery_row stamps onto
 * deliveries.commodity_id. Failing that, the picked block's commodity.
 * null = unknown.
 */
export function lineCommodity<C extends { name: string }>(
  commodities: readonly C[],
  resolve: (raw: string | null | undefined) => string | null,
  variety: string | null | undefined,
  blockCommodity?: string | null,
): C | null {
  const name = resolve(variety) ?? resolve(blockCommodity)
  return name ? commodities.find(c => c.name === name) ?? null : null
}

/**
 * Is this delivery line's fruit drenched? lineCommodity (variety first, then
 * the block's commodity) + commodityUsesDrench, with the block looked up by id
 * in the PUC's block list. One rule for the Gate Intake form and the delivery's
 * edit screen, so a variety the app doesn't recognise falls back to the block
 * on both. A block that isn't in the list (still loading, none picked) gives
 * no fallback, and fruit we can't name keeps the drench question.
 */
export function drenchForLine<C extends { name: string; uses_drench?: boolean | null }>(
  commodities: readonly C[],
  resolve: (raw: string | null | undefined) => string | null,
  blocks: readonly { block_id: string; commodity: string | null }[],
  variety: string | null | undefined,
  blockId: string | null | undefined,
): { commodity: C | null; usesDrench: boolean } {
  const blockCommodity = blockId ? blocks.find(b => b.block_id === blockId)?.commodity ?? null : null
  const commodity = lineCommodity(commodities, resolve, variety, blockCommodity)
  return { commodity, usesDrench: commodityUsesDrench(commodity) }
}

/** 'PEACHES' → 'Peaches' (catalog names are stored upper case). */
export function fruitName(commodityName: string): string {
  const n = commodityName.trim()
  return n.charAt(0).toUpperCase() + n.slice(1).toLowerCase()
}

/** ['PEACHES', 'NECTARINES', 'PEACHES'] → 'Peaches and Nectarines'. */
export function fruitList(commodityNames: string[]): string {
  const names = [...new Set(commodityNames.map(fruitName))]
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}
